import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';


const IncidentForm = ({ onSubmit, autoSaveData, onAutoSave }) => {
  const [formData, setFormData] = useState({
    incidentType: '',
    location: '',
    dateTime: '',
    description: '',
    severity: '',
    probability: '',
    personnelInvolved: '',
    witnessName: '',
    witnessContact: '',
    immediateActions: '',
    followUpActions: '',
    assignee: '',
    reporterName: '',
    reporterPosition: '',
    reporterContact: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const incidentTypeOptions = [
    { value: 'injury', label: 'Personal Injury' },
    { value: 'near-miss', label: 'Near Miss' },
    { value: 'property-damage', label: 'Property Damage' },
    { value: 'environmental', label: 'Environmental Incident' },
    { value: 'security', label: 'Security Breach' },
    { value: 'fire', label: 'Fire/Explosion' },
    { value: 'chemical', label: 'Chemical Spill' },
    { value: 'equipment', label: 'Equipment Failure' }
  ];

  const severityOptions = [
    { value: 'low', label: 'Low - Minor impact' },
    { value: 'medium', label: 'Medium - Moderate impact' },
    { value: 'high', label: 'High - Significant impact' },
    { value: 'critical', label: 'Critical - Severe impact' }
  ];

  const probabilityOptions = [
    { value: 'rare', label: 'Rare - Unlikely to occur' },
    { value: 'unlikely', label: 'Unlikely - May occur' },
    { value: 'possible', label: 'Possible - Might occur' },
    { value: 'likely', label: 'Likely - Will probably occur' },
    { value: 'certain', label: 'Certain - Will definitely occur' }
  ];

  const assigneeOptions = [
    { value: 'safety-manager', label: 'Safety Manager' },
    { value: 'facility-manager', label: 'Facility Manager' },
    { value: 'hr-manager', label: 'HR Manager' },
    { value: 'maintenance-team', label: 'Maintenance Team' },
    { value: 'security-team', label: 'Security Team' }
  ];

  // Auto-save functionality
  useEffect(() => {
    const timer = setTimeout(() => {
      if (Object.values(formData)?.some(value => value !== '')) {
        onAutoSave(formData);
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [formData, onAutoSave]);

  // Load auto-saved data
  useEffect(() => {
    if (autoSaveData) {
      setFormData(autoSaveData);
    }
  }, [autoSaveData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.incidentType) newErrors.incidentType = 'Incident type is required';
    if (!formData?.location) newErrors.location = 'Location is required';
    if (!formData?.dateTime) newErrors.dateTime = 'Date and time is required';
    if (!formData?.description) newErrors.description = 'Description is required';
    if (!formData?.severity) newErrors.severity = 'Severity rating is required';
    if (!formData?.probability) newErrors.probability = 'Probability rating is required';
    if (!formData?.reporterName) newErrors.reporterName = 'Reporter name is required';
    if (!formData?.reporterPosition) newErrors.reporterPosition = 'Reporter position is required';
    if (!formData?.reporterContact) newErrors.reporterContact = 'Reporter contact is required';

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRiskLevel = () => {
    if (!formData?.severity || !formData?.probability) return null;
    
    const severityScore = { low: 1, medium: 2, high: 3, critical: 4 }?.[formData?.severity];
    const probabilityScore = { rare: 1, unlikely: 2, possible: 3, likely: 4, certain: 5 }?.[formData?.probability];
    const riskScore = severityScore * probabilityScore;
    
    if (riskScore <= 4) return { level: 'Low', color: 'text-success bg-success/10' };
    if (riskScore <= 8) return { level: 'Medium', color: 'text-warning bg-warning/10' };
    if (riskScore <= 12) return { level: 'High', color: 'text-error bg-error/10' };
    return { level: 'Critical', color: 'text-error bg-error/20' };
  };

  const riskLevel = getRiskLevel();

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Incident Details Section */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="AlertTriangle" size={20} className="mr-2 text-warning" />
          Incident Details
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Incident Type"
            options={incidentTypeOptions}
            value={formData?.incidentType}
            onChange={(value) => handleInputChange('incidentType', value)}
            error={errors?.incidentType}
            required
            placeholder="Select incident type"
          />
          
          <Input
            label="Location"
            type="text"
            value={formData?.location}
            onChange={(e) => handleInputChange('location', e?.target?.value)}
            error={errors?.location}
            required
            placeholder="Building, floor, room number"
          />
          
          <Input
            label="Date & Time"
            type="datetime-local"
            value={formData?.dateTime}
            onChange={(e) => handleInputChange('dateTime', e?.target?.value)}
            error={errors?.dateTime}
            required
          />
          
          <Input
            label="Personnel Involved"
            type="text"
            value={formData?.personnelInvolved}
            onChange={(e) => handleInputChange('personnelInvolved', e?.target?.value)}
            placeholder="Names of people involved"
          />
        </div>
        
        <div className="mt-4">
          <Input
            label="Detailed Description"
            type="text"
            value={formData?.description}
            onChange={(e) => handleInputChange('description', e?.target?.value)}
            error={errors?.description}
            required
            placeholder="Provide a detailed description of what happened, including sequence of events, conditions, and contributing factors"
            className="min-h-[100px]"
          />
        </div>
      </div>
      {/* Risk Assessment Section */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Target" size={20} className="mr-2 text-primary" />
          Risk Assessment
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Severity Rating"
            options={severityOptions}
            value={formData?.severity}
            onChange={(value) => handleInputChange('severity', value)}
            error={errors?.severity}
            required
            placeholder="Select severity level"
          />
          
          <Select
            label="Probability Rating"
            options={probabilityOptions}
            value={formData?.probability}
            onChange={(value) => handleInputChange('probability', value)}
            error={errors?.probability}
            required
            placeholder="Select probability level"
          />
        </div>
        
        {riskLevel && (
          <div className="mt-4 p-4 rounded-lg border border-border bg-muted/50">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-muted-foreground">Calculated Risk Level:</span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${riskLevel?.color}`}>
                {riskLevel?.level} Risk
              </span>
            </div>
          </div>
        )}
      </div>
      {/* Witness Information Section */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Users" size={20} className="mr-2 text-secondary" />
          Witness Information
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Witness Name"
            type="text"
            value={formData?.witnessName}
            onChange={(e) => handleInputChange('witnessName', e?.target?.value)}
            placeholder="Full name of witness"
          />
          
          <Input
            label="Witness Contact"
            type="text"
            value={formData?.witnessContact}
            onChange={(e) => handleInputChange('witnessContact', e?.target?.value)}
            placeholder="Phone or email"
          />
        </div>
      </div>
      {/* Actions Section */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="CheckSquare" size={20} className="mr-2 text-accent" />
          Actions Taken & Follow-up
        </h3>
        
        <div className="space-y-4">
          <Input
            label="Immediate Actions Taken"
            type="text"
            value={formData?.immediateActions}
            onChange={(e) => handleInputChange('immediateActions', e?.target?.value)}
            placeholder="Describe immediate actions taken to address the incident"
            className="min-h-[80px]"
          />
          
          <Input
            label="Recommended Follow-up Actions"
            type="text"
            value={formData?.followUpActions}
            onChange={(e) => handleInputChange('followUpActions', e?.target?.value)}
            placeholder="Describe recommended actions to prevent recurrence"
            className="min-h-[80px]"
          />
          
          <Select
            label="Assign Follow-up To"
            options={assigneeOptions}
            value={formData?.assignee}
            onChange={(value) => handleInputChange('assignee', value)}
            placeholder="Select responsible person/team"
          />
        </div>
      </div>
      {/* Reporter Information Section */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="User" size={20} className="mr-2 text-primary" />
          Reporter Information
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label="Reporter Name"
            type="text"
            value={formData?.reporterName}
            onChange={(e) => handleInputChange('reporterName', e?.target?.value)}
            error={errors?.reporterName}
            required
            placeholder="Your full name"
          />
          
          <Input
            label="Position/Title"
            type="text"
            value={formData?.reporterPosition}
            onChange={(e) => handleInputChange('reporterPosition', e?.target?.value)}
            error={errors?.reporterPosition}
            required
            placeholder="Your job title"
          />
          
          <Input
            label="Contact Information"
            type="text"
            value={formData?.reporterContact}
            onChange={(e) => handleInputChange('reporterContact', e?.target?.value)}
            error={errors?.reporterContact}
            required
            placeholder="Phone or email"
          />
        </div>
      </div>
      {/* Submit Button */}
      <div className="flex justify-end space-x-4 pt-6 border-t border-border">
        <Button
          type="button"
          variant="outline"
          onClick={() => setFormData({
            incidentType: '',
            location: '',
            dateTime: '',
            description: '',
            severity: '',
            probability: '',
            personnelInvolved: '',
            witnessName: '',
            witnessContact: '',
            immediateActions: '',
            followUpActions: '',
            assignee: '',
            reporterName: '',
            reporterPosition: '',
            reporterContact: ''
          })}
        >
          Clear Form
        </Button>
        
        <Button
          type="submit"
          loading={isSubmitting}
          iconName="Send"
          iconPosition="left"
        >
          Submit Incident Report
        </Button>
      </div>
    </form>
  );
};

export default IncidentForm;